<template>
  <div
    v-if="enableSearchBar"
    ref="searchWrapper"
    :class="{
      'ai-search-bar relative': true,
      'bg-www-color-white': showSuggestions && !mobile,
      '-mx-5 hidden h-full items-center px-5 md:flex': !mobile,
      'mt-10 flex w-full flex-col items-center bg-www-color-transparent': mobile,
    }"
  >
    <form
      :class="{
        'group flex h-5 cursor-text items-center gap-2 transition-colors duration-300': true,
        'text-www-color-white': !showSuggestions,
        'text-www-color-primary-lighter': showSuggestions,
        'hover:text-www-color-primary-lighter': !showSuggestions,
        'px-5': !mobile,
        'w-full px-3 text-www-color-primary-lighter': mobile,
      }"
      @submit.prevent="submitQuery()"
      @click="openSuggestions"
    >
      <PtcIcon
        name="ptc-search"
        :class="{
          'text-xs': true,
          'text-www-color-primary-lighter': showSuggestions,
        }"
      />
      <input
        ref="searchInput"
        v-model="query"
        type="text"
        :aria-label="props.aiAdvisor?.placeholder || 'Search'"
        :placeholder="props.aiAdvisor?.placeholder || 'Search...'"
        :class="{
          'max-w-118px bg-www-color-transparent text-10px font-bold leading-none tracking-wider outline-none placeholder:text-10px placeholder:uppercase placeholder:transition-colors placeholder:duration-300': true,
          'placeholder:text-www-color-white': !showSuggestions,
          'group-hover:placeholder:text-www-color-primary-lighter': !showSuggestions,
          'text-www-color-primary-lighter placeholder:text-www-color-primary-lighter':
            showSuggestions,
        }"
        @focus="openSuggestions"
        @keydown.enter.prevent="submitQuery()"
        @input="onConversationStart({ isFromSearchBar: true })"
      />
    </form>

    <AiSearchSuggestions
      v-if="showSuggestions"
      :mobile="props.mobile"
      :ai-advisor="props.aiAdvisor"
      @select="submitQuery"
    />
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import PtcIcon from '~/components/Atoms/PtcIcon/PtcIcon.vue';
import AiSearchSuggestions from './AiSearchSuggestions.vue';
import { useAiAdvisorSubmitQueryBus } from '../composables/useAiAdvisorBus';
import type { HeaderAiAdvisor } from '~/sanity/types';

const props = defineProps<{
  mobile: boolean;
  aiAdvisor: HeaderAiAdvisor;
  searchUrl: string;
}>();
const enableSearchBar = computed(() => props.aiAdvisor.enableSearchBar ?? false);
const { onConversationStart, storeSearchQuery } = useAiQuery({});
const submitQueryBus = useAiAdvisorSubmitQueryBus();

const query = ref('');
const searchWrapper = ref<HTMLElement | null>(null);
const searchInput = ref<HTMLInputElement | null>(null);
const showSuggestions = ref(props.mobile);

const openSuggestions = () => {
  showSuggestions.value = true;
};

const isCurrentPathSearchTarget = (): boolean => {
  if (!props.searchUrl) return false;

  try {
    const targetUrl = new URL(props.searchUrl, window.location.origin);
    const currentUrl = new URL(useRequestURL().href);

    const normalizePath = (path: string): string => {
      if (path.length > 1 && path.endsWith('/')) {
        return path.slice(0, -1);
      }

      return path;
    };

    return normalizePath(targetUrl.pathname) === normalizePath(currentUrl.pathname);
  } catch (err) {
    console.error('Failed to compare advisor paths:', err);
    return false;
  }
};

const submitQuery = async (suggestedQuery?: string) => {
  const searchTerm = (suggestedQuery ?? query.value).trim();

  if (!searchTerm) {
    openSuggestions();
    searchInput.value?.focus();
    return;
  }

  query.value = searchTerm;
  if (!props.mobile) {
    showSuggestions.value = false;
  }

  storeSearchQuery(searchTerm);

  if (isCurrentPathSearchTarget()) {
    submitQueryBus.emit({ query: searchTerm });
    return;
  }

  location.href = props.searchUrl;
};

onClickOutside(searchWrapper, () => {
  if (props.mobile) return;
  showSuggestions.value = false;
});
</script>

<style>
@keyframes animatedgradient {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
.ai-search-bar::after {
  content: '';
  position: absolute;
  top: -4px;
  width: 100%;
  height: 27px;
  border-radius: 2.1875rem;
  border: 2px solid transparent;
  background: linear-gradient(90deg, #40aa1d, #028cf5 63%, #8013ed) border-box;
  background-size: 300% 300%;
  animation: animatedgradient 3s ease infinite alternate;
  -webkit-mask:
    linear-gradient(#fff 0 0) padding-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  pointer-events: none;
}

@media (min-width: 768px) {
  .ai-search-bar::after {
    top: 4px;
    width: 80%;
  }
}
</style>
